import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Button,
    Spinner,
    useToast,
    Box,
    Heading,
    Input,
    InputGroup,
    InputLeftElement,
    Flex,
    Spacer,
} from "@chakra-ui/react";
import { SearchIcon, CloseIcon, AddIcon } from "@chakra-ui/icons";

const Users = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const toast = useToast();
    const navigate = useNavigate();
    const tableRef = useRef(null);

    const UUID = localStorage.getItem("UUID");
    const HuserID = localStorage.getItem("HuserID");

    const API_BASE_URL = "https://qaapirgeneng.digitalfields.co.za/ws1.cfc";

    const fetchUsers = useCallback(
        async (searchKey = "") => {
            try {
                setLoading(true);
                let url = `${API_BASE_URL}?method=listusers&huserID=${HuserID}&uuid=${UUID}`;

                if (searchKey.length >= 3) {
                    url += `&searchkey=${encodeURIComponent(searchKey)}`;
                }

                const response = await fetch(url, {
                    method: "POST",
                });

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const data = await response.json();
                setUsers(data);
            } catch (error) {
                toast({
                    title: "Error fetching users",
                    description:
                        "There was an error fetching the users. Please try again.",
                    status: "error",
                    duration: 5000,
                    isClosable: true,
                });
            } finally {
                setLoading(false);
            }
        },
        [HuserID, UUID, toast]
    );

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    const handleSearch = () => {
        if (searchTerm.length >= 3 || searchTerm.length === 0) {
            fetchUsers(searchTerm);
        } else {
            toast({
                title: "Search term too short",
                description: "Please enter at least 3 characters to search",
                status: "warning",
                duration: 3000,
                isClosable: true,
            });
        }
    };

    const handleKeyUp = (event) => {
        if (event.key === "Enter") {
            handleSearch();
        }
    };

    const clearSearch = () => {
        setSearchTerm("");
        fetchUsers("");
    };

    const handleViewEdit = (user) => {
        navigate(`/user/${user.huserid}`, {
            state: { scrollPosition: tableRef.current?.scrollTop },
        });
    };

    useEffect(() => {
        if (tableRef.current && window.history.state?.scrollPosition) {
            tableRef.current.scrollTop = window.history.state.scrollPosition;
        }
    }, []);

    if (loading) {
        return (
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="100vh"
            >
                <Spinner size="xl" />
            </Box>
        );
    }

    return (
        <Box p={5}>
            <Heading mb={5}>Users List</Heading>
            <Flex align="center" mb={5}>
                <Button
                    variant="outline-rounded"
                    leftIcon={<AddIcon />}
                    onClick={() => navigate("/adduser")}
                >
                    Add User
                </Button>
                <Spacer />
                <Flex gap={2} alignItems="center">
                    <InputGroup width="300px">
                        {searchTerm.length === 0 && (
                            <InputLeftElement pointerEvents="none">
                                <SearchIcon color="gray.400" />
                            </InputLeftElement>
                        )}
                        <Input
                            placeholder="Search users (min. 3 chars)"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onKeyUp={handleKeyUp}
                        />
                    </InputGroup>
                    <Button
                        onClick={handleSearch}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        w="40px"
                        p={0}
                    >
                        <SearchIcon />
                    </Button>
                    <Button
                        onClick={clearSearch}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        w="40px"
                        p={0}
                    >
                        <CloseIcon />
                    </Button>
                </Flex>
            </Flex>
            <Box ref={tableRef} overflowY="auto" maxHeight="70vh">
                <Table variant="striped">
                    <Thead>
                        <Tr>
                            <Th>First Name</Th>
                            <Th>Last Name</Th>
                            <Th>Email</Th>
                            <Th>Role</Th>
                            <Th>Cell Number</Th>
                            <Th>Active</Th>
                            <Th>Actions</Th>
                        </Tr>
                    </Thead>
                    <Tbody>
                        {users.length > 0 ? (
                            users.map((user, index) => (
                                <Tr key={`${user.fname}-${user.sname}-${index}`}>
                                    <Td>{user.fname}</Td>
                                    <Td>{user.sname}</Td>
                                    <Td>{user.email}</Td>
                                    <Td>{user.role}</Td>
                                    <Td>{user.cellNumber || "-"}</Td>
                                    <Td>{user.activeYN === 1 ? "Yes" : "No"}</Td>
                                    <Td>
                                        <Button
                                            variant="outline-rounded"
                                            onClick={() => handleViewEdit(user)}
                                        >
                                            View/Edit
                                        </Button>
                                    </Td>
                                </Tr>
                            ))
                        ) : (
                            <Tr>
                                <Td colSpan={7} textAlign="center">
                                    No users found
                                </Td>
                            </Tr>
                        )}
                    </Tbody>
                </Table>
            </Box>
        </Box>
    );
};

export default Users;